import { Injectable } from '@angular/core';
import { Provider } from '@interfaces/provider.model';
import { Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppParamsService } from '@services/app.params.service';
import { CompareParams } from '@interfaces/compare-params.model';
import { CompareProvider } from '@interfaces/compare-provider.model';
import { Location } from '@angular/common';
import { SearchParamType } from '@interfaces/search-param-type.interface';
import { MatCheckbox } from '@angular/material/checkbox';

@Injectable({
  providedIn: 'root',
})
export class CompareService {
  public compareTrayStatus = new Subject<boolean>();
  public providers: Provider[] = [];
  public lastCheckboxToggled: MatCheckbox;
  private LIMIT = 4;
  private compareParams: CompareParams;
  private providerParams: any;

  constructor(
    private router: Router,
    private http: HttpClient,
    private appParamsService: AppParamsService,
    private location: Location
  ) {}

  public change(
    event,
    provider,
    compareCheckbox: MatCheckbox,
    keyboard?: boolean
  ) {
    this.lastCheckboxToggled = compareCheckbox;
    // Event is toggled via native checkbox checked value
    if (event.checked !== undefined && !keyboard) {
      if (event.checked) {
        this.addProvider(provider);
      } else {
        this.deleteProvider(provider);
      }
    }
    // Event toggled via keyboard
    if (keyboard) {
      if (!compareCheckbox.checked) {
        this.addProvider(provider, true);
      } else {
        this.deleteProvider(provider, true);
      }
    }
  }

  public addProvider(provider: Provider, keyboard?: boolean): void {
    if (this.providers.length < this.LIMIT) {
      if (!this.providers.find((prov) => prov.id === provider.id)) {
        this.providers.push(provider);
        if (keyboard) {
          this.compareTrayStatus.next(true);
        }
      }
    }
  }

  public deleteProvider(provider: Provider, keyboard?: boolean): void {
    this.providers = this.providers.filter((prov) => prov.id !== provider.id);

    if (this.providers.length === 0 && keyboard) {
      this.compareTrayStatus.next(false);
    }
  }

  public selectedProvider(provider: Provider): boolean {
    return !!this.providers.find((prov) => prov.id === provider.id);
  }

  public compareEnabled(): boolean {
    return this.providers.length < this.LIMIT;
  }

  public clearProviders(): void {
    this.compareTrayStatus.next(false);
    this.providers = [];
  }

  public getComparedProviders(
    providerParams: any,
    referrerParams: any
  ): Observable<any[]> {
    this.providerParams = providerParams;
    this.compareParams = referrerParams;
    return this.requestProviders().pipe(first((providers) => !!providers));
  }

  public goToCompare(
    searchParamType: SearchParamType,
    lastSearchParams: HttpParams,
    providers?: any[]
  ): void {
    const params = JSON.stringify(
      this.setSearchParams(searchParamType, lastSearchParams)
    );
    const route = [
      '/compare',
      JSON.stringify(this.mapProvidersForParams(providers)),
      params,
    ];
    // Update route without triggering router event
    if (providers) {
      const url = this.router.createUrlTree(route);
      this.location.replaceState(this.router.serializeUrl(url));
    } else {
      this.router.navigate(route, { queryParamsHandling: 'preserve' });
    }
  }

  private mapProvidersForParams(providers?: any): any {
    if (!providers) {
      providers = this.providers;
    }
    const out = [];
    providers.forEach((provider) => {
      out.push({
        provider_id: provider.provider_id,
        location_id: provider.location_id,
      });
    });
    return out;
  }

  private setSearchParams(
    searchParamType: SearchParamType,
    searchParams: HttpParams
  ): CompareParams {
    return new CompareParams({
      searchParamType: searchParamType,
      name: searchParams?.get('name'),
      fulltext: searchParams?.get('fulltext'),
      procedure_id: searchParams?.get('procedure_id'),
      search_specialty_id: searchParams?.get('search_specialty_id'),
      radius: searchParams?.get('radius'),
      geo_location: searchParams?.get('geo_location'),
      network_id: searchParams?.get('network_id'),
      account_id: searchParams?.get('account_id'),
    });
  }

  private requestProviders(): Observable<CompareProvider[]> {
    const url = `/api/providers/compare.json`;
    const params = this.getRequestParams();
    return this.http.get(url, { withCredentials: true, params: params }).pipe(
      catchError(() => of({ providers: [] })),
      map((response: any) => response['providers'])
    );
  }

  private getRequestParams(): any {
    let mappedParams = this.appParamsService.setHttpParams(this.compareParams);
    const memberParams = this.appParamsService.getMemberParams();
    if (memberParams) {
      mappedParams = mappedParams.append('dob', memberParams.dob);
      mappedParams = mappedParams.append('gender', memberParams.gender);
      mappedParams = mappedParams.append(
        'member_number',
        memberParams.member_number
      );
    }
    this.providerParams.forEach((provider) => {
      mappedParams = mappedParams.append('provider_id[]', provider.provider_id);
      mappedParams = mappedParams.append('location_id[]', provider.location_id);
    });
    return mappedParams;
  }
}
